import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Eye, RefreshCw, Wallet } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_VARIANT = { pending: 'outline', paid: 'default', rejected: 'destructive' };

export default function AdminPayouts() {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({ pending_count: 0, paid_count: 0, rejected_count: 0 });
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const [actionOpen, setActionOpen] = useState(null); // 'confirm' | 'reject' | null
  const [transferRef, setTransferRef] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/payout-requests${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`;
      const res = await axios.get(url);
      setRequests(res.data.requests || []);
      setSummary(res.data.summary || { pending_count: 0, paid_count: 0, rejected_count: 0 });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load payout requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openDetail = async (requestId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payout-requests/${requestId}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load details');
    }
  };

  const submitConfirm = async () => {
    if (!detail?.request) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/payout-requests/${detail.request.request_id}/confirm`, {
        paystack_transfer_reference: transferRef || null,
        admin_notes: adminNotes || null
      });
      toast.success('Payout confirmed as paid');
      setActionOpen(null);
      setDetailOpen(false);
      setTransferRef('');
      setAdminNotes('');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (!detail?.request) return;
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/payout-requests/${detail.request.request_id}/reject`, {
        reason: rejectReason,
        admin_notes: adminNotes || null
      });
      toast.success('Payout request rejected');
      setActionOpen(null);
      setDetailOpen(false);
      setRejectReason('');
      setAdminNotes('');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-payouts-tab">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Pending</p>
          <p className="text-2xl font-bold text-amber-500" data-testid="payouts-pending-count">{summary.pending_count}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Paid</p>
          <p className="text-2xl font-bold text-green-500" data-testid="payouts-paid-count">{summary.paid_count}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Rejected</p>
          <p className="text-2xl font-bold text-red-500" data-testid="payouts-rejected-count">{summary.rejected_count}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Affiliate Payout Requests</span>
            <div className="flex items-center gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]" data-testid="payouts-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={fetchRequests} data-testid="payouts-refresh-btn">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-6 text-muted-foreground">Loading…</p>
          ) : requests.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground">No payout requests found.</p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-left p-3 font-medium">Affiliate</th>
                    <th className="text-center p-3 font-medium">Items</th>
                    <th className="text-right p-3 font-medium">Amount</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {requests.map(r => (
                    <tr key={r.request_id} data-testid={`admin-payout-row-${r.request_id}`}>
                      <td className="p-3 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{r.affiliate_name || 'Unknown'}</span>
                          <span className="text-xs text-muted-foreground">{r.affiliate_email || r.affiliate_phone || '—'}</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">{r.commission_ids?.length || 0}</td>
                      <td className="p-3 text-right font-medium">{r.currency} {(r.total_amount || 0).toLocaleString()}</td>
                      <td className="p-3 text-center">
                        <Badge variant={STATUS_VARIANT[r.status] || 'outline'}>{r.status}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(r.request_id)} data-testid={`payout-view-${r.request_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payout Request Details</DialogTitle>
            <DialogDescription>Review the request and confirm or reject.</DialogDescription>
          </DialogHeader>
          {detail?.request && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Request:</span> <code className="text-xs">{detail.request.request_id}</code></div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant={STATUS_VARIANT[detail.request.status] || 'outline'}>{detail.request.status}</Badge></div>
                <div><span className="text-muted-foreground">Affiliate:</span> {detail.request.affiliate_name}</div>
                <div><span className="text-muted-foreground">Email:</span> {detail.request.affiliate_email || '—'}</div>
                <div><span className="text-muted-foreground">Phone:</span> {detail.request.affiliate_phone || '—'}</div>
                <div><span className="text-muted-foreground">Total:</span> <strong>{detail.request.currency} {(detail.request.total_amount || 0).toLocaleString()}</strong></div>
                <div className="col-span-2 border-t pt-2">
                  <p className="text-muted-foreground text-xs">Paystack Subaccount</p>
                  <p><code className="text-xs">{detail.request.subaccount_code}</code></p>
                  <p className="text-xs">{detail.request.account_name} — ••••{(detail.request.account_number || '').slice(-4)} ({detail.request.settlement_bank})</p>
                </div>
                {detail.request.notes && <div className="col-span-2"><span className="text-muted-foreground">Affiliate notes:</span> {detail.request.notes}</div>}
                {detail.request.admin_notes && <div className="col-span-2"><span className="text-muted-foreground">Admin notes:</span> {detail.request.admin_notes}</div>}
                {detail.request.rejection_reason && <div className="col-span-2"><span className="text-muted-foreground">Rejection reason:</span> {detail.request.rejection_reason}</div>}
                {detail.request.paystack_transfer_reference && <div className="col-span-2"><span className="text-muted-foreground">Transfer ref:</span> <code className="text-xs">{detail.request.paystack_transfer_reference}</code></div>}
              </div>

              {/* Commissions table */}
              {detail.commissions?.length > 0 && (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Commission ID</th>
                        <th className="text-left p-2">Event</th>
                        <th className="text-right p-2">Amount</th>
                        <th className="text-center p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detail.commissions.map(c => (
                        <tr key={c.commission_id}>
                          <td className="p-2 font-mono">{c.commission_id}</td>
                          <td className="p-2 font-mono">{c.event_id?.slice(0, 16)}…</td>
                          <td className="p-2 text-right font-medium">{c.currency} {(c.commission_amount || 0).toLocaleString()}</td>
                          <td className="p-2 text-center"><Badge variant="outline">{c.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {detail.request.status === 'pending' && (
                <DialogFooter className="gap-2">
                  <Button variant="destructive" onClick={() => setActionOpen('reject')} data-testid="payout-reject-btn">
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button onClick={() => setActionOpen('confirm')} data-testid="payout-confirm-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm as Paid
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={actionOpen === 'confirm'} onOpenChange={(o) => !o && setActionOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payout as Paid</DialogTitle>
            <DialogDescription>
              Record the Paystack transfer reference (if any) and optional notes. This will mark all commissions as paid and update affiliate balances.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="transferRef">Paystack Transfer Reference (optional)</Label>
              <Input id="transferRef" value={transferRef} onChange={(e) => setTransferRef(e.target.value)} placeholder="TRF_XXX..." data-testid="payout-confirm-transfer-ref" />
            </div>
            <div>
              <Label htmlFor="confirmNotes">Admin Notes (optional)</Label>
              <Textarea id="confirmNotes" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={3} data-testid="payout-confirm-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionOpen(null)}>Cancel</Button>
            <Button onClick={submitConfirm} disabled={submitting} data-testid="payout-confirm-submit">
              {submitting ? 'Confirming…' : 'Confirm as Paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={actionOpen === 'reject'} onOpenChange={(o) => !o && setActionOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payout Request</DialogTitle>
            <DialogDescription>
              This will unlock the commissions so the affiliate can request again. Please provide a clear reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rejectReason">Reason (required)</Label>
              <Textarea id="rejectReason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} required data-testid="payout-reject-reason" />
            </div>
            <div>
              <Label htmlFor="rejectNotes">Admin Notes (optional)</Label>
              <Textarea id="rejectNotes" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} data-testid="payout-reject-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionOpen(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitReject} disabled={submitting} data-testid="payout-reject-submit">
              {submitting ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
